import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Store, ShoppingBag, TrendingUp, Smile } from 'lucide-react';

export const BusinessesBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const benefits = {
    consumers: [
      {
        icon: ShoppingBag,
        text: 'גלו עסקים ייחודיים בקרבת מקום',
      },
      {
        icon: Smile,
        text: 'תמכו בבעלי עסקים מקומיים',
      },
    ],
    businesses: [
      {
        icon: Store,
        text: 'הגיעו לקהל המקומי שלכם בקלות',
      },
      {
        icon: TrendingUp,
        text: 'צרו חנות אינטרנטית בפשטות',
      },
    ],
  };

  return (
    <section ref={ref} className="relative py-12 sm:py-16 px-6 bg-gradient-to-br from-surface to-secondary-100 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-1/4 w-2 h-2 bg-brand-400 rounded-full animate-pulse" />
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-secondary-500 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-brand-500 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-l from-brand-700 via-brand-600 to-brand-700 leading-tight mb-3">
            פשוט לצרוך מקומית
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-brand-700 font-medium"
          >
            יתרונות לחברי הקהילה ולעסקים
          </motion.p>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
          {/* For Consumers */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-5 sm:p-6 md:p-8 shadow-xl border border-brand-400/10 h-full">
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-secondary-500/10 to-transparent rounded-tr-3xl" />

              <div className="relative">
                {/* Badge */}
                <div className="inline-block bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full text-base sm:text-base md:text-lg font-bold mb-4 sm:mb-5 md:mb-6 shadow-lg">
                  לחברי הקהילה
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-brand-700 mb-6 sm:mb-7 md:mb-8">
                  למה זה טוב עבורכם?
                </h3>

                <div className="space-y-6">
                  {benefits.consumers.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                      className="flex items-start gap-4 group"
                    >
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl blur-md opacity-30 group-hover:opacity-50 transition-opacity" />
                        <div className="relative bg-gradient-to-br from-secondary-500 to-secondary-600 p-3 rounded-xl">
                          <benefit.icon className="w-6 h-6 text-white" strokeWidth={2} />
                        </div>
                      </div>
                      <p className="text-base sm:text-lg md:text-xl text-brand-700 font-medium leading-relaxed pt-2">
                        {benefit.text}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Decorative illustration placeholder */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="mt-10 flex justify-center"
                >
                  <div className="relative w-40 h-40 bg-gradient-to-br from-secondary-500/20 to-secondary-600/20 rounded-full flex items-center justify-center">
                    <ShoppingBag className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 text-secondary-500" strokeWidth={1.5} />
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* For Businesses */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-5 sm:p-6 md:p-8 shadow-xl border border-brand-400/10 h-full">
              {/* Decorative corner */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-brand-400/10 to-transparent rounded-tl-3xl" />

              <div className="relative">
                {/* Badge */}
                <div className="inline-block bg-gradient-to-r from-brand-400 to-brand-500 text-white px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full text-base sm:text-base md:text-lg font-bold mb-4 sm:mb-5 md:mb-6 shadow-lg">
                  לבעלי עסקים
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-brand-700 mb-6 sm:mb-7 md:mb-8">
                  למה זה טוב עבורכם?
                </h3>

                <div className="space-y-6">
                  {benefits.businesses.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                      className="flex items-start gap-4 group"
                    >
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-brand-500 rounded-xl blur-md opacity-30 group-hover:opacity-50 transition-opacity" />
                        <div className="relative bg-gradient-to-br from-brand-400 to-brand-500 p-3 rounded-xl">
                          <benefit.icon className="w-6 h-6 text-white" strokeWidth={2} />
                        </div>
                      </div>
                      <p className="text-base sm:text-lg md:text-xl text-brand-700 font-medium leading-relaxed pt-2">
                        {benefit.text}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Decorative illustration placeholder */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="mt-10 flex justify-center"
                >
                  <div className="relative w-40 h-40 bg-gradient-to-br from-brand-400/20 to-brand-500/20 rounded-full flex items-center justify-center">
                    <Store className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 text-brand-400" strokeWidth={1.5} />
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
